import { useState, useEffect, useRef, useCallback } from 'react';
import { MenuItem } from '../types';

const HOVER_DELAY = 30;

/**
 * Hook isolé : observe une seule fois les changements de classe sur body.
 * À appeler au niveau de MuiSidebarMenu, pas dans chaque MenuItem.
 */
export const useIsCollapsed = (): boolean => {
    const [isCollapsed, setIsCollapsed] = useState(
        () => document.body.classList.contains('vertical-collpsed')
    );

    useEffect(() => {
        const check = () => setIsCollapsed(document.body.classList.contains('vertical-collpsed'));
        const observer = new MutationObserver(check);
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    return isCollapsed;
};

export const useCollapsedMenu = (isCollapsed: boolean) => {
    const [popperAnchor, setPopperAnchor] = useState<HTMLElement | null>(null);
    const [showPopper, setShowPopper] = useState(false);
    const [hoveredSubItem, setHoveredSubItem] = useState<MenuItem | null>(null);
    const [subMenuAnchor, setSubMenuAnchor] = useState<HTMLElement | null>(null);
    const [showSubMenu, setShowSubMenu] = useState(false);
    const timeoutRefs = useRef<NodeJS.Timeout[]>([]);

    const clearAllTimeouts = useCallback(() => {
        timeoutRefs.current.forEach(clearTimeout);
        timeoutRefs.current = [];
    }, []);

    const addTimeout = useCallback((callback: () => void, delay: number) => {
        clearAllTimeouts();
        const timeout = setTimeout(callback, delay);
        timeoutRefs.current.push(timeout);
    }, [clearAllTimeouts]);

    useEffect(() => {
        return () => clearAllTimeouts();
    }, [clearAllTimeouts]);

    const closeMenus = useCallback(() => {
        setShowPopper(false);
        setPopperAnchor(null);
        setShowSubMenu(false);
        setSubMenuAnchor(null);
        setHoveredSubItem(null);
    }, []);

    const handleMouseEnter = useCallback((e: React.MouseEvent, hasChildren: boolean, level: number) => {
        if (isCollapsed && level === 0 && hasChildren) {
            clearAllTimeouts();
            setPopperAnchor(e.currentTarget as HTMLElement);
            setShowPopper(true);
        }
    }, [isCollapsed, clearAllTimeouts]);

    const handleMouseLeave = useCallback((level: number) => {
        if (isCollapsed && level === 0) {
            addTimeout(closeMenus, HOVER_DELAY);
        }
    }, [isCollapsed, closeMenus, addTimeout]);

    const handlePopperMouseEnter = useCallback(() => {
        if (isCollapsed) {
            clearAllTimeouts();
            setShowPopper(true);
        }
    }, [isCollapsed, clearAllTimeouts]);

    const handlePopperMouseLeave = useCallback(() => {
        if (isCollapsed) {
            addTimeout(closeMenus, HOVER_DELAY);
        }
    }, [isCollapsed, closeMenus, addTimeout]);

    const handleSubItemHover = useCallback((subItem: MenuItem, event: React.MouseEvent) => {
        clearAllTimeouts();
        if (subItem.subMenu?.length) {
            setHoveredSubItem(subItem);
            setSubMenuAnchor(event.currentTarget as HTMLElement);
            setShowSubMenu(true);
        } else {
            addTimeout(() => {
                setShowSubMenu(false);
                setSubMenuAnchor(null);
                setHoveredSubItem(null);
            }, HOVER_DELAY);
        }
    }, [clearAllTimeouts, addTimeout]);

    const handleSubItemLeave = useCallback(() => {
        addTimeout(() => {
            setShowSubMenu(false);
            setSubMenuAnchor(null);
            setHoveredSubItem(null);
        }, HOVER_DELAY);
    }, [addTimeout]);

    const handleSubMenuMouseEnter = useCallback(() => {
        clearAllTimeouts();
    }, [clearAllTimeouts]);

    const handleSubMenuMouseLeave = useCallback(() => {
        addTimeout(() => {
            setShowSubMenu(false);
            setSubMenuAnchor(null);
            setHoveredSubItem(null);
        }, HOVER_DELAY);
    }, [addTimeout]);

    return {
        popperAnchor,
        showPopper,
        hoveredSubItem,
        subMenuAnchor,
        showSubMenu,
        closeMenus,
        handleMouseEnter,
        handleMouseLeave,
        handlePopperMouseEnter,
        handlePopperMouseLeave,
        handleSubItemHover,
        handleSubItemLeave,
        handleSubMenuMouseEnter,
        handleSubMenuMouseLeave
    };
};
